import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Endpoint } from '@/Config/api/endpoint';
import i18n from '@/Config/app/i18n';
import { McpApi } from '@/Domain/api/Action/Mcp.api';
import { McpSettingDto } from '@/Domain/api/Action/dto/McpSetting.dto';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';

const McpFormValidator = z.object({
  active: z.boolean(),
  api_key_required: z.boolean(),
  visibility: z.enum(['public', 'private']),
  name: z.string().max(100).min(1),
  description: z.string().max(750).min(1),
  command: z
    .string()
    .max(40)
    .regex(/^[A-Za-z_]*$/, i18n.t('mcp_page:form.validation.letter_underscore_only'))
    .optional(),
});

export type McpSettingForm = z.infer<typeof McpFormValidator>;

export const useMcpForm = () => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: [Endpoint.mcp.get],
    queryFn: () => McpApi.get(),
  });

  const form = useForm<McpSettingForm>({
    shouldFocusError: false,
    defaultValues: {
      active: !!data?.active,
      api_key_required: !!data?.api_key_required,
      visibility: data?.visibility || 'private',
      command: data?.command || '',
      name: data?.name || '',
      description: data?.description || '',
    },
    resolver: zodResolver(McpFormValidator),
  });

  const mutation = useMutation({
    mutationKey: [Endpoint.mcp.patch],
    mutationFn: (data: Partial<McpSettingForm>) => {
      queryClient.setQueryData([Endpoint.mcp.get], (prevData: McpSettingDto) => ({
        ...prevData,
        ...data,
      }));

      return McpApi.patch(data);
    },
    onError: (_, prevData) => {
      queryClient.setQueryData([Endpoint.mcp.get], (data: McpSettingDto) => ({
        ...data,
        ...prevData,
      }));
    },
  });

  const handleMutation = (data: Partial<McpSettingForm>) => mutation.mutate(data);

  useAutoSubmit(form, (data) => handleMutation(data as McpSettingForm), false);

  return {
    data,
    form,
    handleMutation,
  };
};
